import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { Icon } from '@iconify/react'
import { useModal } from '@ebay/nice-modal-react'
import {
  Button,
  KeywordBadge,
  useUpdateReport,
  useShowMessage,
} from '@pepper/ui'

import type {
  Keyword,
  ReportType,
  ResearchReportDocument,
} from '@pepper/utils/src/types/seo'

import {
  getColorFromGrade,
  getGapMetrics,
  getSectionHeadingColor,
} from './ContentGuidelines.utils'
import { useSelectedCompanyId } from 'utils/functions/misc'

import { getMetaCopy } from 'container/SEOTools/ContentGapReport/Insights/utils'
import { generateGradeObject } from '@pepper/docs/src/components/SEOPaneV2/utils/content-grade'
import { Grade } from '@pepper/docs/src/components/SEOPaneV2/components/ContentGrade/ContentGrade.types'
import { calculateContentGrade } from '@pepper/docs/src/components/SEOPaneV2/components/ContentGrade/ContentGrade.utils'
import { getInitialMetricFormValues } from '../../EditModals/schema'
import contentMetric from './contentMetric'
import LoadingBar from 'components/LoadingBar'
import AddKeywordBucketModal from '../../EditModals/AddKeywordModal'
import EditMetricsForm from '../../EditModals/EditMetricsForm'
import Card from '../../../../../../SEOTools/ContentGapReport/Insights/Card/Card'

import styles from './ContentGuidelines.module.css'

const LOADING_BAR_COUNT = 5
const RECOMMENDED_TERMS_COUNT = 10
const MAX_META_TITLE_LENGTH = 60
const MIN_META_TITLE_LENGTH = 50
const MAX_META_DESCRIPTION_LENGTH = 160
const MIN_META_DESCRIPTION_LENGTH = 150

interface Props {
  primaryKeyword: Keyword
  secondaryKeywords?: Keyword[]
  generationStatusId?: number
  report?: ResearchReportDocument | null
  reportId?: string
  itemId?: string
  reportType?: ReportType
}

const getHeaderIcon = (color?: string | null, grade?: Grade) => {
  const val = grade ? getColorFromGrade(grade) : color
  switch (val) {
    case 'GREEN':
      return (
        <Icon
          icon="lucide:circle-check"
          color="var(--color-green-500)"
          width={24}
          height={24}
        />
      )
    case 'YELLOW':
      return (
        <Icon
          icon="material-symbols:error-outline"
          color="var(--color-yellow-600)"
          width={24}
          height={24}
        />
      )
    case 'RED':
      return <Icon icon="lucide:x-circle" color="red" width={24} height={24} />
    default:
      return <Icon icon="uil:lightbulb-alt" />
  }
}

function ContentGuidelines({
  reportId,
  report,
  itemId,
  generationStatusId,
  primaryKeyword,
  secondaryKeywords,
  reportType,
}: Props): JSX.Element {
  const companyId = useSelectedCompanyId()
  const updateReport = useUpdateReport()
  const showMessage = useShowMessage()
  const [activeGradeKey, setActiveGradeKey] = useState<Grade>()
  const [metricIconColor, setMetricIconColor] = useState<
    'GREEN' | 'YELLOW' | 'RED'
  >()

  const [editKeywords, setEditKeywords] = useState<boolean>(false)

  const [keywords, setKeywords] = useState<{
    primary: Keyword[]
    secondary: Keyword[]
    terms: Keyword[]
  }>({
    primary: [primaryKeyword],
    secondary: secondaryKeywords ?? [],
    terms: [],
  })
  const editMetricsModal = useModal(EditMetricsForm)
  const addKeywordModal = useModal(AddKeywordBucketModal)

  const isLoadingReport = generationStatusId
    ? [1, 2, 4].includes(generationStatusId)
    : true

  const isLoadingSecondaryKeywords =
    isLoadingReport && !secondaryKeywords?.length

  const handleKeywordSubmit = () => {
    if (!reportId) return
    updateReport.mutate(
      {
        companyId,
        reportId,
        body: {
          document: {
            secondary_keywords: keywords.secondary,
            terms: keywords.terms,
          },
        },
      },

      {
        onSuccess: () => {
          showMessage({ type: 'success', message: 'Report updated' })
          setEditKeywords(false)
        },
        onError: error => {
          setEditKeywords(false)
          if (error instanceof Error) {
            showMessage({ type: 'error', message: error.message })
          }
        },
      }
    )
  }

  const handleUpdateKeywordState = (values: {
    action: 'delete'
    type: 'secondary' | 'terms'
    index: number
  }) => {
    const updatedKeyword = {
      ...keywords,
      [values.type]: keywords[values.type].filter(
        (_, index) => index !== values.index
      ),
    }
    setKeywords(updatedKeyword)
  }

  const getLoadingBar = (loaderCount: number) => {
    return new Array(loaderCount).fill(null)
  }

  useEffect(() => {
    setKeywords({
      primary: report?.primary_keyword ? [report?.primary_keyword] : [],
      secondary: report?.secondary_keywords ?? secondaryKeywords ?? [],
      terms: report?.terms ?? [],
    })
  }, [
    report?.primary_keyword,
    report?.secondary_keywords,
    report?.terms,
    secondaryKeywords,
  ])

  const getSectionTitles = () => {
    if (reportType === 'GAP') {
      return {
        primary: 'Suggested Primary Keyword',
        secondary: 'Secondary keywords you should be using',
        recommended: 'Recommended terms to add in the content',
      }
    } else
      return {
        primary: 'Primary Keyword',
        secondary: 'Secondary Keywords',
        recommended: 'Recommended Terms',
      }
  }
  const renderSecondaryKeywords = () => {
    if (keywords.secondary.length > 0 && !report?.secondary_keywords) {
      return (
        <div className={styles.keywordContent}>
          <h4 className={styles.keywordSubHeader}>
            {getSectionTitles().secondary}
          </h4>
          <div className={styles.keywordList}>
            {keywords.secondary.map((secondary, index) => (
              <div className={styles.keywordBadge} key={index}>
                {secondary.keyword}
                <LoadingBar width={40} height={12} borderRadius={20} />
              </div>
            ))}
          </div>
        </div>
      )
    }
    const secondaryKeywords = keywords.secondary.filter(
      keyword => !(keyword.min === keyword.max && keyword.min === 0)
    )
    return secondaryKeywords.length > 0 ? (
      <div className={styles.keywordContent}>
        <h4 className={styles.keywordSubHeader}>
          {getSectionTitles().secondary}
        </h4>
        <div className={styles.keywordList}>
          {keywords.secondary.map((keyword, index) => (
            <KeywordBadge
              key={`${keyword.keyword + index}`}
              data={keyword}
              activeCount={keyword.page?.count}
              isDelete={editKeywords}
              backgroundType={
                reportType === 'GAP' || editKeywords ? 'default' : 'gray'
              }
              handleDelete={() =>
                handleUpdateKeywordState({
                  action: 'delete',
                  type: 'secondary',
                  index,
                })
              }
              isActiveHeadingKeyword={
                keyword.page?.heading_presence ? true : false
              }
            />
          ))}
          {editKeywords ? (
            <Button
              className={styles.addKeywordButton}
              size="small"
              icon={<Icon icon="uil:plus" />}
              onClick={() =>
                addKeywordModal.show({
                  reportId,
                  keywords,
                  type: 'secondary',
                  onSuccess: () => setEditKeywords(false),
                })
              }
            >
              Add New
            </Button>
          ) : null}
        </div>
      </div>
    ) : null
  }

  const targetGrade = useMemo(() => {
    if (reportType !== 'GAP') return null
    if (!report) return null

    return generateGradeObject(report)
  }, [report, reportType])

  const activeGrade = useMemo(() => {
    if (reportType !== 'GAP') return null
    if (!report) return null

    const headingKeywordCount: (0 | 1)[] = []

    const primary = report.primary_keyword ? [report.primary_keyword] : null
    const primaryKeywordCount = primary?.map(item => {
      if (item.heading_presence >= 2) {
        const val = item.page?.heading_presence ?? 0
        headingKeywordCount.push(val >= 1 ? 1 : 0)
      }
      return item.page?.count ?? 0
    })
    const secondaryKeywordCount = report.secondary_keywords?.map(item => {
      if (item.heading_presence >= 2) {
        const val = item.page?.heading_presence ?? 0
        headingKeywordCount.push(val >= 1 ? 1 : 0)
      }
      return item.page?.count ?? 0
    })
    const termsKeywordCount = report.terms?.map(item => {
      if (item.heading_presence >= 2) {
        const val = item.page?.heading_presence ?? 0
        headingKeywordCount.push(val >= 1 ? 1 : 0)
      }
      return item.page?.count ?? 0
    })

    return {
      document_length: report.page_stats.length,
      readability_grade: report.page_stats.reading_grade,
      headings: report.page_stats.element_counts.hx,
      paragraph_density: report.page_stats.element_counts.p,
      image_count: report.page_stats.element_counts.img,
      link_tally: report.page_stats.element_counts.a,
      keyword_optimization: {
        primary: primaryKeywordCount ?? [],
        secondary: secondaryKeywordCount ?? [],
        recommended: termsKeywordCount ?? [],
        heading_presence: headingKeywordCount ?? [],
      },
    }
  }, [report, reportType])

  useEffect(() => {
    if (!activeGrade || !targetGrade) return

    const contentGrade = calculateContentGrade(activeGrade, targetGrade)
    setActiveGradeKey(contentGrade.grade)
  }, [activeGrade, targetGrade])

  useEffect(() => {
    if (reportType === 'GAP' && report) {
      const metricColorArr = getGapMetrics(report).map(item => item.color.color)
      const color = getSectionHeadingColor(metricColorArr)
      setMetricIconColor(color)
    }
  }, [report, reportType])

  return (
    <div className={styles.container}>
      <section className={styles.section}>
        <header className={styles.header}>
          <div className={styles.heading}>
            {getHeaderIcon(metricIconColor)}
            <h3>Content Metrics</h3>
          </div>
          {!itemId && reportType !== 'GAP' ? (
            <Button
              size="small"
              onClick={() => {
                if (!report) return
                editMetricsModal.show({
                  initialFormValues: getInitialMetricFormValues(report),
                  reportId,
                })
              }}
              disabled={!report}
              icon={<Icon icon="lucide:pen-square" />}
            >
              Edit
            </Button>
          ) : null}
        </header>
        <div className={styles.metrics}>
          {!report
            ? getLoadingBar(LOADING_BAR_COUNT).map((_, index) => (
                <div
                  key={index}
                  className={clsx(styles.loadingMetric, styles.metricItem)}
                >
                  <LoadingBar borderRadius={4} width={120} />
                  <LoadingBar borderRadius={4} />
                </div>
              ))
            : contentMetric(reportType, report)}
        </div>
      </section>
      <section className={styles.section}>
        <header className={styles.header}>
          <div className={styles.heading}>
            {reportType === 'GAP'
              ? getHeaderIcon(null, activeGradeKey)
              : getHeaderIcon()}
            <h3>Keywords and Terms</h3>
          </div>
          {!itemId && reportType !== 'GAP' ? (
            editKeywords ? (
              <Button
                size="small"
                loading={updateReport.isLoading}
                onClick={handleKeywordSubmit}
              >
                Save
              </Button>
            ) : (
              <Button
                size="small"
                onClick={() => setEditKeywords(true)}
                icon={<Icon icon="lucide:pen-square" />}
                disabled={!report}
              >
                Edit
              </Button>
            )
          ) : null}
        </header>
        <div className={clsx(styles.keywords)}>
          <div className={styles.keywordContent}>
            <h4 className={styles.keywordSubHeader}>
              {getSectionTitles().primary}
            </h4>
            <div className={styles.keywordList}>
              {report?.primary_keyword && (
                <KeywordBadge
                  key={report?.primary_keyword.keyword}
                  data={report?.primary_keyword}
                  activeCount={report.primary_keyword.page?.count}
                  backgroundType={reportType === 'GAP' ? 'default' : 'gray'}
                  isActiveHeadingKeyword={
                    report?.primary_keyword.page?.heading_presence
                      ? true
                      : false
                  }
                />
              )}
              {primaryKeyword && !report?.primary_keyword && (
                <div className={styles.keywordBadge}>
                  {primaryKeyword.keyword}
                  <LoadingBar width={40} height={12} borderRadius={20} />
                </div>
              )}
            </div>
          </div>

          {isLoadingSecondaryKeywords ? (
            <div className={styles.keywordContent}>
              <h4 className={styles.keywordSubHeader}>
                {getSectionTitles().secondary}
              </h4>
              <div className={styles.secondaryKeywordsLoader}>
                {getLoadingBar(LOADING_BAR_COUNT).map((_, index) => (
                  <LoadingBar key={index} borderRadius={4} />
                ))}
              </div>
            </div>
          ) : (
            renderSecondaryKeywords()
          )}

          <div className={styles.keywordContent}>
            <h4 className={styles.keywordSubHeader}>
              {getSectionTitles().recommended}
            </h4>
            <div className={styles.keywordList}>
              {keywords.terms.length === 0 &&
                getLoadingBar(RECOMMENDED_TERMS_COUNT).map((_, index) => (
                  <LoadingBar key={index} borderRadius={4} />
                ))}
              {keywords.terms.map((terms, index) => (
                <KeywordBadge
                  key={`${terms.keyword + index}`}
                  isDelete={editKeywords}
                  backgroundType={
                    reportType === 'GAP' || editKeywords ? 'default' : 'gray'
                  }
                  handleDelete={() =>
                    handleUpdateKeywordState({
                      action: 'delete',
                      type: 'terms',
                      index,
                    })
                  }
                  activeCount={terms.page?.count}
                  data={terms}
                  isActiveHeadingKeyword={
                    terms.page?.heading_presence ? true : false
                  }
                />
              ))}
              {editKeywords ? (
                <Button
                  className={styles.addKeywordButton}
                  size="small"
                  icon={<Icon icon="uil:plus" />}
                  onClick={() =>
                    addKeywordModal.show({
                      reportId,
                      keywords,
                      type: 'terms',
                      onSuccess: () => setEditKeywords(false),
                    })
                  }
                >
                  Add New
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </section>
      {reportType === 'GAP' && report?.page_stats.meta ? (
        <section className={styles.metaDataContainer}>
          <Card
            type={
              report?.page_stats.meta.title.length <= MAX_META_TITLE_LENGTH &&
              report?.page_stats.meta.title.length >= MIN_META_TITLE_LENGTH &&
              report?.page_stats.meta.title.keyword_present
                ? 'success'
                : 'error'
            }
            title="Meta Title"
            reportType={reportType}
            subTitle={getMetaCopy(
              {
                target_keyword_stats: report.target_keyword_stats,
                meta: report.page_stats.meta,
              },
              'title'
            )}
          >
            <p className={styles.text}>
              {report?.page_stats.meta?.title.content.length > 0
                ? report?.page_stats.meta?.title.content
                : 'NA'}
            </p>
          </Card>
          <Card
            type={
              report?.page_stats.meta.description.length <=
                MAX_META_DESCRIPTION_LENGTH &&
              report?.page_stats.meta.description.length >=
                MIN_META_DESCRIPTION_LENGTH &&
              report?.page_stats.meta.description.keyword_present
                ? 'success'
                : 'error'
            }
            title="Meta Description"
            reportType={reportType}
            subTitle={getMetaCopy(
              {
                target_keyword_stats: report.target_keyword_stats,
                meta: report.page_stats.meta,
              },
              'description'
            )}
          >
            <p className={styles.text}>
              {report?.page_stats.meta.description.content.length > 0
                ? report?.page_stats.meta.description.content
                : 'NA'}
            </p>
          </Card>
          {report.page_stats.questions ? (
            <Card
              type="success"
              title="FAQs"
              subTitle="The FAQ schema is present"
              reportType={reportType}
            />
          ) : (
            <Card
              type="error"
              title="FAQs"
              subTitle="The FAQ schema is not present."
              reportType={reportType}
            >
              Adding FAQ schema to your content helps the search engines to
              feature the questions and answers as rich snippets directly in
              search results driving traffic to your link. Learn more from{' '}
              <a
                href="https://developers.google.com/search/docs/appearance/structured-data/faqpage"
                target="_blank"
                rel="noreferrer"
                className={styles.externalLink}
              >
                here.
              </a>
            </Card>
          )}
        </section>
      ) : null}
    </div>
  )
}

export default ContentGuidelines
